"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { base } from "./defaults";
import { Editor } from "./Editor";
import PreviewItem from "./PreviewItem";
import "react-perfect-scrollbar/dist/css/styles.css";
import Analyzer from "./Analyzer";
import { AppContext } from "./AppContext";
import Exporter from "./Exporter";
import { icons } from "./icons";
import Randomizer from "./Randomizer";
import Button from "./Button";
import History from "./History";
import CustomBg from "./CustomBg";
import Scale from "./Scale";
import FavsBrowser from "./FavsBrowser";
import SaveDB from "./SaveDB";
import DarkMode from "./DarkMode";
import Login from "./Login";
import ColorPreviewer from "../ColorPreviewer";

export default function EditorInterface({ receivedData }) {
	const getLocallyStoredData = () => {
		try {
			const ls = localStorage.getItem("heroMesherCourtesyBackup");

			return JSON.parse(ls);
		} catch (err) {
			return false;
		}
	};

	const startupData = receivedData
		? receivedData.data
		: getLocallyStoredData()
		? getLocallyStoredData()
		: {
				config: base,
				bg: "hsla(0,100%,50%,1)",
		  };

	const defaultMeshName = "New Mesh";
	const loadBg = startupData.bg;
	const loadConfig = startupData.config;
	const loadTitle = receivedData ? receivedData.name : defaultMeshName;
	const [config, setConfig] = useState(loadConfig);
	const [bg, setBg] = useState(loadBg);
	const [session, setSession] = useState(Date.now());
	const [showSaved, setShowSaved] = useState(false);
	const [copyColor, setCopyColor] = useState(false);
	const [colorHilight, setColorHilight] = useState(null);
	const [duration, setDuration] = useState("10s");
	const [animationType, setAnimationType] = useState("alternate");
	const [showGrid, setShowGrid] = useState(false);
	const [openLeaf, setOpenLeaf] = useState(null);
	const prevContainer = useRef(null);
	const [meshName, setMeshName] = useState(loadTitle);
	const [scale, setScale] = useState(1);
	const [isEditing, setIsEditing] = useState(false);
	const [showExport, setShowExport] = useState(false);
	const [customBg, setCustomBg] = useState(false);
	const [showFavs, setShowFavs] = useState(false);
	const [hueRotation, setHueRotation] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const prevShadow = scale === 1 ? "" : "shadow-lg rounded ";

	const prev = useMemo(
		() => (
			<>
				{prevContainer.current && showGrid && (
					<Analyzer prevContainer={prevContainer} />
				)}

				<PreviewItem
					className={prevShadow + " absolute inset-0"}
					duration={duration}
					config={config}
					bg={customBg || bg}
					animationType={animationType}
				/>
			</>
		),
		[
			config,
			bg,
			prevContainer,
			duration,
			animationType,
			showGrid,
			customBg,
			prevShadow,
		]
	);
	const refreshGrid = () => {
		if (showGrid) {
			setShowGrid(false);
			setTimeout(() => {
				setShowGrid(true);
			}, 200);
		}
	};

	const ediCl = isEditing
		? ["max-w-sm pl-2", "opacity-0"]
		: ["max-w-[0px]", "opacity-100"];
	const ctxValue = {
		hueRotation,
		setHueRotation,
		refreshGrid,
		meshName,
		setMeshName,
		isSaving,
		setIsSaving,
		receivedData,
		showGrid,
		setShowGrid,
		customBg,
		showFavs,
		setShowFavs,
		setCustomBg,
		config,
		setConfig,
		bg,
		setBg,
		copyColor,
		setCopyColor,
		duration,
		setDuration,
		animationType,
		setAnimationType,
		colorHilight,
		setColorHilight,
		showSaved,
		setShowSaved,
		session,
		setSession,
		openLeaf,
		setOpenLeaf,
		setScale,
		scale,
		setShowExport,
		setIsEditing,
		isEditing,
		ediCl,
	};

	useEffect(() => {
		if (!receivedData) {
			const storeOb = { bg, config };
			localStorage.setItem(
				"heroMesherCourtesyBackup",
				JSON.stringify(storeOb)
			);
		}
	}, [bg, config, receivedData]);

	const expCl = showExport
		? ["-ml-[800px]"]
		: showFavs
		? ["ml-[800px]"]
		: ["ml-0"];

	const returnScaleStyle = (c) => {
		return {
			width: c * scale + "%",
			height: c * scale + "%",
		};
	};

	const grid = useMemo(() => {
		return (
			<div className="grid-rows-10 absolute inset-0 grid grid-cols-10">
				{Array.from(Array(200).keys()).map((z, k) => (
					<div className="gridEl">
						<div />
						<div />
					</div>
				))}
			</div>
		);
	}, []);

	return (
		<AppContext.Provider value={ctxValue}>
			<div
				className={
					expCl[0] +
					" fixed inset-0 z-[9999] flex h-screen w-screen bg-white transition-all dark:bg-dark-900"
				}
			>
				{showExport && <Exporter />}
				<FavsBrowser />
				<div className="absolute inset-2 flex rounded-lg ">
					<div className="relative flex flex-1 flex-col gap-2 transition-all">
						<div className="flex h-full flex-1 overflow-hidden">
							<div className="group relative z-30 flex flex-1 items-center justify-center overflow-hidden rounded-lg bg-dark-100 dark:bg-dark-950">
								<div className="flexer relative h-full w-full flex-1 ">
									{grid}
									<div
										ref={prevContainer}
										className="relative z-50 rounded bg-white"
										style={returnScaleStyle(100)}
									>
										{prev}
									</div>
								</div>
							</div>
							<div
								className={
									ediCl[0] +
									" z-50 flex h-full flex-1 flex-col overflow-hidden bg-white transition-all dark:bg-dark-900"
								}
							>
								<Editor />
							</div>
							{!isEditing && <ColorPreviewer />}
						</div>

						<div className="z-[999] flex gap-2">
							<div className="flex flex-1 items-center gap-4 ">
								<Login className="rounded-full bg-dark-100 pr-2 dark:bg-dark-800" />
								<DarkMode cl="barButton" />
								<Scale />

								<Button
									onClick={() => setShowGrid((g) => !g)}
									className="barButton"
								>
									{icons.grid}
									{showGrid && (
										<div className="absolute right-0 top-0 h-2 w-2 rounded-full bg-accent-500" />
									)}
								</Button>
							</div>
							<div className="flex items-center">
								<div className="relative flex items-center gap-4 ">
									<CustomBg />
									<Randomizer />
								</div>
							</div>
							<div className="flex flex-1 items-center justify-end gap-2 ">
								<History cl="barButton" />

								<Button
									className="bg-accent-400/10 px-3 text-accent-500"
									onClick={() => setShowExport((c) => !c)}
								>
									{icons.export}
									Export
								</Button>
								<SaveDB />
							</div>
						</div>
					</div>
				</div>
			</div>
		</AppContext.Provider>
	);
}
